import ENV from "./Environment";
import Requests from "./Requests";

const getApiBaseUrl = () => {
    const rawBaseUrl = (ENV.base_url || "").trim().replace(/\/+$/, "");
    if (!rawBaseUrl) {
        return "";
    }
    if (rawBaseUrl.endsWith("/api/v1")) {
        return rawBaseUrl;
    }
    return `${rawBaseUrl}/api/v1`;
};

const baseUrl = getApiBaseUrl();

const normalizeToArray = (data) => {
    if (data == null) {
        return [];
    }
    if (Array.isArray(data)) {
        return data;
    }
    if (Array.isArray(data?.content)) {
        return data.content;
    }
    if (Array.isArray(data?.data)) {
        return data.data;
    }
    if (Array.isArray(data?.results)) {
        return data.results;
    }
    if (Array.isArray(data?.assets)) {
        return data.assets;
    }
    if (Array.isArray(data?.data?.content)) {
        return data.data.content;
    }
    if (Array.isArray(data?.data?.data)) {
        return data.data.data;
    }
    return [];
};

const isPlainObject = (value) =>
    value != null && typeof value === "object" && !Array.isArray(value);

const getResponseBody = async (response) => {
    const text = await response.clone().text();
    if (text == null || !String(text).trim()) {
        return null;
    }
    try {
        return JSON.parse(text);
    } catch {
        return { message: text };
    }
};

const getError = (data, response) => {
    const responseUrl =
        response && typeof response.url === "string" && response.url.trim()
            ? response.url.trim()
            : "";
    const statusPrefix =
        response && typeof response.status === "number"
            ? `${response.status}${response.statusText ? ` ${response.statusText}` : ""}: `
            : "";
    if (!data) {
        return `${statusPrefix}Unexpected server response.${responseUrl ? ` Endpoint: ${responseUrl}` : ""}`.trim();
    }
    if (typeof data === "string" && data.trim()) {
        return `${statusPrefix}${data}`.trim();
    }
    const msg =
        data.error ||
        data.message ||
        data.detail ||
        data.title ||
        data?.data?.message ||
        (Array.isArray(data.errors) ? data.errors.join(", ") : null);
    if (msg && String(msg).trim()) {
        return `${statusPrefix}${String(msg).trim()}`.trim();
    }
    return `${statusPrefix}An unexpected error occurred.${responseUrl ? ` Endpoint: ${responseUrl}` : ""}`.trim();
};

const mapAsset = (asset) => ({
    assetKey: String(asset?.assetKey ?? asset?.id ?? asset?._id ?? ""),
    name: asset?.name || "",
    sourceBriefUrl: asset?.sourceBriefUrl || "",
    assignedQaUser:
        asset?.assignedQaUser ||
        asset?.qaUser ||
        asset?.assignee ||
        asset?.assignedTo ||
        "",
    qaAssetType: asset?.type || asset?.qaAssetType || "plugin",
    status: asset?.status || "Pending QA",
    createdBy: asset?.createdBy ?? null,
    createdAt: asset?.createdAt || "",
    lastUpdateBy:
        asset?.lastUpdateBy ??
        asset?.lastUpdatedBy ??
        asset?.updatedBy ??
        asset?.modifiedBy ??
        asset?.createdBy ??
        asset?.assignedQaUser ??
        asset?.qaUser ??
        asset?.assignee ??
        asset?.assignedTo ??
        "",
    lastUpdated: asset?.lastUpdated ?? asset?.updatedAt ?? "",
});

const createQaAsset = async (userData, payload) => {
    const requestBody = {
        name: payload.name,
        sourceBriefUrl: payload.sourceBriefUrl,
        type: payload.qaAssetType || "plugin",
        assignedQaUser: payload.assignedQaUser || null,
    };

    const response = await Requests.postRequest(
        `${baseUrl}/asset/save`,
        userData.jwtToken,
        requestBody,
        userData
    );
    const data = await getResponseBody(response);
    if (!response.ok) {
        throw new Error(getError(data, response));
    }
    return mapAsset(data?.data || data);
};

const getQaAssets = async (userData) => {
    const response = await Requests.getRequest(
        `${baseUrl}/asset/list`,
        userData.jwtToken,
        userData,
        false
    );
    const data = await getResponseBody(response);
    if (!response.ok) {
        throw new Error(getError(data, response));
    }
    return normalizeToArray(data).map(mapAsset).filter((asset) => asset.assetKey);
};

const deleteQaAsset = async (userData, assetId) => {
    const id = String(assetId || "").trim();
    if (!id) {
        throw new Error("Asset id is required to delete.");
    }
    const response = await Requests.deleteRequest(
        `${baseUrl}/asset/${encodeURIComponent(id)}`,
        userData
    );
    const data = await getResponseBody(response);
    if (!response.ok) {
        throw new Error(getError(data, response));
    }
    return data;
};

const normalizeChecklistItems = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }
    return items.map((item, index) => ({
        id: String(item?.id ?? item?._id ?? item?.itemId ?? index),
        text: String(item?.title ?? item?.name ?? item?.text ?? "").trim(),
        checked: Boolean(item?.checked),
    }));
};

const getChecklistTitle = (
    checklist,
    index,
    fallbackGroupName = "",
    templateTitleMap = {}
) => {
    const templateLinkId = String(
        checklist?.templateId ??
        checklist?.template_id ??
        checklist?.template?.id ??
        checklist?.template?._id ??
        checklist?.template?._templateId ??
        checklist?.template?._template_id ??
        ""
    ).trim();

    if (templateLinkId && templateTitleMap[templateLinkId]) {
        return templateTitleMap[templateLinkId];
    }

    const directTitle =
        checklist?.title ??
        checklist?.name ??
        checklist?.templateName ??
        checklist?.template ??
        checklist?.header ??
        checklist?.heading ??
        checklist?.category ??
        checklist?.checklistName;

    if (directTitle != null && String(directTitle).trim()) {
        return String(directTitle).trim();
    }

    const rawItems =
        checklist?.items ??
        checklist?.checklistItems ??
        checklist?.content ??
        checklist?.data;

    if (Array.isArray(rawItems) && rawItems.length > 0) {
        const firstItem = rawItems[0] || {};
        const itemDerivedTitle =
            firstItem?.groupName ??
            firstItem?.group ??
            firstItem?.templateName ??
            firstItem?.category ??
            firstItem?.sectionName ??
            firstItem?.section;

        if (itemDerivedTitle != null && String(itemDerivedTitle).trim()) {
            return String(itemDerivedTitle).trim();
        }
    }

    if (fallbackGroupName && String(fallbackGroupName).trim()) {
        return String(fallbackGroupName).trim();
    }

    return `Checklist ${index + 1}`;
};

const normalizeChecklists = (data, templateTitleMap = {}) => {
    const list = normalizeToArray(data);
    if (list.length) {
        return list.map((checklist, index) => ({
            id: String(checklist?.id ?? checklist?._id ?? checklist?.checklistId ?? index),
            title: getChecklistTitle(checklist, index, "", templateTitleMap),
            description: checklist?.description ? String(checklist.description) : "",
            items: normalizeChecklistItems(
                checklist?.items ??
                checklist?.checklistItems ??
                checklist?.content ??
                checklist?.data
            ),
        }));
    }

    if (isPlainObject(data)) {
        const entries = Object.entries(data).filter(([, value]) => Array.isArray(value));
        for (const [groupName, value] of entries) {
            return value.map((checklist, index) => ({
                id: String(checklist?.id ?? checklist?._id ?? checklist?.checklistId ?? `${groupName}-${index}`),
                title: getChecklistTitle(checklist, index, groupName, templateTitleMap),
                description: checklist?.description ? String(checklist.description) : "",
                items: normalizeChecklistItems(
                    checklist?.items ??
                    checklist?.checklistItems ??
                    checklist?.content ??
                    checklist?.data
                ),
            }));
        }
    }

    return [];
};

const getTemplateTitleMap = async (userData) => {
    const response = await Requests.getRequest(
        `${baseUrl}/admin/templates`,
        userData.jwtToken,
        userData,
        false
    );
    const data = await getResponseBody(response);
    if (!response.ok) {
        return {};
    }

    const templates = normalizeToArray(data);
    const titleMap = {};
    templates.forEach((template) => {
        const id = String(
            template?.templateId ??
            template?.id ??
            template?._id ??
            template?.uuid ??
            template?.template_id ??
            ""
        ).trim();
        const title = String(
            template?.name ??
            template?.title ??
            template?.templateName ??
            ""
        ).trim();
        if (id && title) {
            titleMap[id] = title;
        }
    });
    return titleMap;
};

const getAssetChecklists = async (userData, assetId) => {
    const checklistEndpoints = [
        `${baseUrl}/assets/${assetId}/checklists`,
    ];

    const templateTitleMapPromise = getTemplateTitleMap(userData);
    let lastError = null;

    for (const endpoint of checklistEndpoints) {
        const checklistsResponse = await Requests.getRequest(
            endpoint,
            userData.jwtToken,
            userData,
            false
        );
        const data = await getResponseBody(checklistsResponse);
        if (!checklistsResponse.ok) {
            lastError = new Error(getError(data, checklistsResponse));
            continue;
        }
        const templateTitleMap = await templateTitleMapPromise;
        return normalizeChecklists(data, templateTitleMap);
    }

    throw new Error(lastError?.message || "Unable to load checklists.");
};

const updateChecklistItem = async (userData, checklistId, itemId, checked) => {
    const response = await Requests.putRequest(
        `${baseUrl}/checklists/${checklistId}/items/${itemId}`,
        userData.jwtToken,
        { checked },
        userData,
        false
    );
    const data = await getResponseBody(response);
    if (!response.ok) {
        throw new Error(getError(data, response));
    }
    return data;
};

const QA_LAST_ACCESS_STORAGE_PREFIX = "qa-last-access-";

const parseAssetTimestampMs = (value) => {
    if (value == null || value === "") {
        return 0;
    }
    const t = Date.parse(String(value).trim());
    return Number.isFinite(t) ? t : 0;
};

const getLastAccessedMs = (assetKey) => {
    if (!assetKey) {
        return 0;
    }
    try {
        const raw = localStorage.getItem(`${QA_LAST_ACCESS_STORAGE_PREFIX}${assetKey}`);
        return parseAssetTimestampMs(raw);
    } catch {
        return 0;
    }
};

/**
 * Marks an asset as recently opened (QA detail / checklist). Used to keep the list
 * ordered with the last-accessed asset at the top together with created/updated times.
 */
const recordQaAssetLastAccessed = (assetKey) => {
    if (!assetKey) {
        return;
    }
    try {
        localStorage.setItem(
            `${QA_LAST_ACCESS_STORAGE_PREFIX}${assetKey}`,
            new Date().toISOString()
        );
    } catch {
        // ignore quota / private mode
    }
};

const clearQaAssetLastAccessed = (assetKey) => {
    if (!assetKey) {
        return;
    }
    try {
        localStorage.removeItem(`${QA_LAST_ACCESS_STORAGE_PREFIX}${assetKey}`);
    } catch {
        // ignore
    }
};

const clearQaProgressLocal = (assetKey) => {
    if (!assetKey) {
        return;
    }
    try {
        localStorage.removeItem(`qa-progress-${assetKey}`);
    } catch {
        // ignore
    }
};

const QA_STAGE_STORAGE_PREFIX = "qa-stage-";
const QA_STAGE_SEQUENCE = ["Briefs", "Audio", "Design", "Coding"];

const getStageKeyword = (stage) => {
    const normalized = String(stage || "").trim().toLowerCase();
    if (normalized === "briefs") {
        return "brief";
    }
    return normalized;
};

const deriveQaStageFromChecklists = (checklists = []) => {
    if (!Array.isArray(checklists) || checklists.length === 0) {
        return "Briefs";
    }

    for (const stage of QA_STAGE_SEQUENCE) {
        const stageKeyword = getStageKeyword(stage);
        const matchingChecklist = checklists.find((checklist) =>
            String(checklist?.title || "")
                .trim()
                .toLowerCase()
                .startsWith(stageKeyword)
        );

        if (!matchingChecklist) {
            return stage;
        }

        const items = Array.isArray(matchingChecklist.items)
            ? matchingChecklist.items
            : [];
        const isStageComplete = items.length > 0 && items.every((item) => Boolean(item?.checked));
        if (!isStageComplete) {
            return stage;
        }
    }

    return "Completed";
};

const recordQaAssetStage = (assetKey, stage) => {
    if (!assetKey || !stage) {
        return;
    }
    try {
        localStorage.setItem(`${QA_STAGE_STORAGE_PREFIX}${assetKey}`, String(stage));
    } catch {
        // ignore quota / private mode
    }
};

const getRecordedQaAssetStage = (assetKey) => {
    if (!assetKey) {
        return "";
    }
    try {
        return localStorage.getItem(`${QA_STAGE_STORAGE_PREFIX}${assetKey}`) || "";
    } catch {
        return "";
    }
};

const clearQaAssetStage = (assetKey) => {
    if (!assetKey) {
        return;
    }
    try {
        localStorage.removeItem(`${QA_STAGE_STORAGE_PREFIX}${assetKey}`);
    } catch {
        // ignore
    }
};

/**
 * Higher value = should appear nearer the top of the QA Processes list.
 * Uses max(last opened, server lastUpdated, server createdAt).
 */
const getQaAssetListSortKeyMs = (asset) => {
    const accessed = getLastAccessedMs(asset?.assetKey);
    const updated = parseAssetTimestampMs(asset?.lastUpdated);
    const created = parseAssetTimestampMs(asset?.createdAt);
    return Math.max(accessed, updated, created);
};

const sortQaAssetsNewestFirst = (assets) =>
    [...assets].sort((a, b) => getQaAssetListSortKeyMs(b) - getQaAssetListSortKeyMs(a));

const QaProcessService = {
    createQaAsset,
    getQaAssets,
    deleteQaAsset,
    getAssetChecklists,
    updateChecklistItem,
    recordQaAssetLastAccessed,
    clearQaAssetLastAccessed,
    clearQaProgressLocal,
    deriveQaStageFromChecklists,
    recordQaAssetStage,
    getRecordedQaAssetStage,
    clearQaAssetStage,
    sortQaAssetsNewestFirst,
};

export default QaProcessService;


